import React, { useEffect, useState, useMemo } from "react";
import { Search, CheckCheck, XCircle, Plus, ChevronDown } from "lucide-react";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  PENDING:    "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  APPROVED:   "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  REJECTED:   "text-red-400 bg-red-500/10 border-red-500/20",
  OVERRIDDEN: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  CANCELLED:  "text-white/25 bg-white/5 border-white/10",
};

const TYPE_STYLE = {
  Annual:    "text-[#00ff90]", Sick:      "text-red-400",
  Casual:    "text-blue-400",  Unpaid:    "text-yellow-400",
  Maternity: "text-purple-400",Paternity: "text-cyan-400",
};

export default function RequestsTab({ onOpenEmployee }) {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [selected,  setSelected]  = useState(new Set());
  const [bulkBusy,  setBulkBusy]  = useState(null);
  const [bulkNote,  setBulkNote]  = useState("");
  const [showBulk,  setShowBulk]  = useState(false);
  const [showAdd,   setShowAdd]   = useState(false);
  const [comments,  setComments]  = useState({});

  const [filters, setFilters] = useState({
    search: "", status: "ALL", type: "ALL", dept: "ALL",
    from: "", to: "",
  });

  // Add-on-behalf form
  const [addForm, setAddForm] = useState({
    employee_id: "", leave_type: "Annual", from: "", to: "", reason: "",
  });
  const [addBusy, setAddBusy] = useState(false);
  const [addMsg,  setAddMsg]  = useState(null);

  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : data?.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    let out = [...items];
    if (filters.status !== "ALL") out = out.filter(x => x.status === filters.status);
    if (filters.type   !== "ALL") out = out.filter(x => x.type   === filters.type);
    if (filters.dept   !== "ALL") out = out.filter(x => x.dept   === filters.dept);
    if (filters.from)             out = out.filter(x => x.startDate >= filters.from);
    if (filters.to)               out = out.filter(x => x.endDate   <= filters.to);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      out = out.filter(x =>
        (x.employeeName || "").toLowerCase().includes(q) ||
        (x.employeeId   || "").toLowerCase().includes(q) ||
        (x.dept         || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [items, filters]);

  const allSelected  = visible.length > 0 && visible.every(x => selected.has(x.id));
  const someSelected = visible.some(x => selected.has(x.id));
  const toggleAll    = () => setSelected(allSelected ? new Set() : new Set(visible.map(x => x.id)));
  const toggleOne    = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const setComment = (id, v) => setComments(c => ({ ...c, [id]: v }));

  const act = async (id, action, note = "") => {
    try {
      await fetch(`${API_BASE}/api/admin/leave-rules/request/${id}/${action}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
      setItems(prev => prev.map(x => x.id === id ? { ...x, status: action === "approve" ? "APPROVED" : action === "reject" ? "REJECTED" : "OVERRIDDEN" } : x));
    } catch (e) { setError(e.message); }
  };

  const bulkAct = async (action) => {
    setBulkBusy(action);
    try {
      await fetch(`${API_BASE}/api/admin/leave-rules/bulk-action`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action, ids: [...selected], note: bulkNote }),
      });
      const status = action === "approve" ? "APPROVED" : "REJECTED";
      setItems(prev => prev.map(x => selected.has(x.id) ? { ...x, status } : x));
      setSelected(new Set()); setBulkNote(""); setShowBulk(false);
    } catch (e) { setError(e.message); }
    finally { setBulkBusy(null); }
  };

  const addLeave = async () => {
    if (!addForm.employee_id || !addForm.from || !addForm.to) {
      setAddMsg({ type: "error", text: "Employee ID, from and to dates are required." }); return;
    }
    setAddBusy(true); setAddMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/add-on-behalf`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(addForm),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAddMsg({ type: "success", text: "Leave added successfully." });
      setAddForm({ employee_id: "", leave_type: "Annual", from: "", to: "", reason: "" });
      await load();
    } catch (e) { setAddMsg({ type: "error", text: e.message }); }
    finally { setAddBusy(false); }
  };

  const depts = useMemo(() => ["ALL", ...new Set(items.map(x => x.dept).filter(Boolean))], [items]);
  const types = useMemo(() => ["ALL", ...new Set(items.map(x => x.type).filter(Boolean))], [items]);

  return (
    <div className="space-y-4">

      {/* Filter bar */}
      <div className={`${card} p-4`}>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[180px]">
            <Search size={13} className="text-[#00ff90]/40 shrink-0" />
            <input className={`${inputCls} flex-1`} placeholder="Search name, ID, dept…"
              value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          </div>
          {[
            { key: "status", opts: ["ALL","PENDING","APPROVED","REJECTED","OVERRIDDEN","CANCELLED"] },
            { key: "type",   opts: types },
            { key: "dept",   opts: depts },
          ].map(({ key, opts }) => (
            <select key={key} className={inputCls} value={filters[key]}
              onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}>
              {opts.map(o => <option key={o} value={o}>{o === "ALL" ? `All ${key}s` : o}</option>)}
            </select>
          ))}
          <input type="date" className={inputCls} value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          <input type="date" className={inputCls} value={filters.to}   onChange={e => setFilters(f => ({ ...f, to:   e.target.value }))} />
          <button onClick={() => setShowAdd(v => !v)}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors ml-auto">
            <Plus size={12} /> Add on Behalf
          </button>
        </div>
      </div>

      {/* Add on behalf form */}
      {showAdd && (
        <div className={`${card} p-5 space-y-3`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Add Leave on Behalf of Employee</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className={labelCls}>Employee ID</label>
              <input className={`${inputCls} w-full`} value={addForm.employee_id} onChange={e => setAddForm(f => ({ ...f, employee_id: e.target.value }))} placeholder="e.g. E001" /></div>
            <div><label className={labelCls}>Leave Type</label>
              <select className={`${inputCls} w-full`} value={addForm.leave_type} onChange={e => setAddForm(f => ({ ...f, leave_type: e.target.value }))}>
                {["Annual","Sick","Casual","Unpaid","Maternity","Paternity"].map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>From</label>
              <input type="date" className={`${inputCls} w-full`} value={addForm.from} onChange={e => setAddForm(f => ({ ...f, from: e.target.value }))} /></div>
            <div><label className={labelCls}>To</label>
              <input type="date" className={`${inputCls} w-full`} value={addForm.to} onChange={e => setAddForm(f => ({ ...f, to: e.target.value }))} /></div>
            <div className="md:col-span-2"><label className={labelCls}>Reason</label>
              <input className={`${inputCls} w-full`} value={addForm.reason} onChange={e => setAddForm(f => ({ ...f, reason: e.target.value }))} placeholder="Admin-initiated reason" /></div>
          </div>
          {addMsg && <p className={`text-xs ${addMsg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{addMsg.text}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={addLeave} disabled={addBusy}
              className="text-xs px-5 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {addBusy ? "Adding…" : "Add Leave"}
            </button>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-3 flex-wrap rounded-xl border border-[#00ff90]/20 bg-[#00ff90]/5 px-4 py-3">
          <span className="text-xs text-[#00ff90]/70 font-medium">{selected.size} selected</span>
          <input placeholder="Note (optional)" value={bulkNote} onChange={e => setBulkNote(e.target.value)}
            className="bg-black border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none w-40" />
          <button onClick={() => bulkAct("approve")} disabled={bulkBusy !== null}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] disabled:opacity-40 transition-colors">
            <CheckCheck size={12} /> Bulk Approve
          </button>
          <button onClick={() => bulkAct("reject")} disabled={bulkBusy !== null}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-500/25 bg-red-500/8 text-red-400/70 hover:bg-red-500/15 hover:text-red-400 disabled:opacity-40 transition-colors">
            <XCircle size={12} /> Bulk Reject
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-white/30 hover:text-white ml-auto transition-colors">Clear</button>
        </div>
      )}

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* Table */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Leave Requests</span>
          <span className="text-xs text-white/30">{visible.length} of {items.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 rounded accent-[#00ff90]" />
                </th>
                {["Employee","Type","Dates","Days","Dept","Status","Conflicts",""].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}><td colSpan={9} className="px-4 py-3"><div className="h-8 rounded bg-white/5 animate-pulse" /></td></tr>
                ))
              ) : visible.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-white/20 italic text-sm">No requests found.</td></tr>
              ) : visible.map(r => {
                const typeColor  = TYPE_STYLE[r.type]   || "text-white/50";
                const statusStyle = STATUS_STYLE[r.status] || STATUS_STYLE.PENDING;
                const isPending  = !r.status || r.status === "PENDING";
                return (
                  <tr key={r.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${selected.has(r.id) ? "bg-[#00ff90]/3" : ""}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} className="w-4 h-4 rounded accent-[#00ff90]" />
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => onOpenEmployee?.(r.employeeId, r.employeeName)}
                        className="text-left hover:text-[#00ff90] transition-colors">
                        <p className="text-sm text-white/80 font-medium">{r.employeeName}</p>
                        <p className="text-xs text-white/30">{r.employeeId}</p>
                      </button>
                    </td>
                    <td className="px-3 py-3"><span className={`text-xs font-medium ${typeColor}`}>{r.type}</span></td>
                    <td className="px-3 py-3 text-white/50 text-xs whitespace-nowrap">
                      {r.startDate} → {r.endDate}
                    </td>
                    <td className="px-3 py-3 text-white/50 text-xs">{r.days}</td>
                    <td className="px-3 py-3 text-white/40 text-xs">{r.dept}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>{r.status || "PENDING"}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        {r.conflicts?.blackout      && <span className="text-xs text-red-400/60">🚫</span>}
                        {r.conflicts?.staffingBelow && <span className="text-xs text-orange-400/60">⚠</span>}
                        {r.conflicts?.teamOff > 0   && <span className="text-xs text-yellow-400/60">👥{r.conflicts.teamOff}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {isPending ? (
                        <div className="flex items-center gap-1.5">
                          <input placeholder="Note…" value={comments[r.id] || ""} onChange={e => setComment(r.id, e.target.value)}
                            className="bg-black border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none w-24" />
                          <button onClick={() => act(r.id, "approve", comments[r.id] || "")}
                            className="text-xs px-2 py-1 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">✓</button>
                          <button onClick={() => act(r.id, "reject", comments[r.id] || "")}
                            className="text-xs px-2 py-1 rounded-lg border border-red-500/20 bg-red-500/5 text-red-400/60 hover:bg-red-500/15 hover:text-red-400 transition-colors">✗</button>
                        </div>
                      ) : (
                        <button onClick={() => act(r.id, "cancel")}
                          className="text-xs px-2 py-1 rounded-lg border border-white/10 text-white/25 hover:text-white/50 transition-colors">Cancel</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}